<template>
    <div class="alias-row" :class="{'mobile': getMobileAppState}">
        <div class="icon-text-wrapper">
            <!-- Warning Icon -->
            <warning-icon
            v-if="aliasList.find(el => el.aliasType === aliasType) && aliasList.find(el => el.aliasType === aliasType).aliasStatus?.aliasStatus === 'F'"
            class="warning-icon"/>
            <!-- Success Icon -->
            <check-icon v-else-if="aliasList.find(el => el.aliasType === aliasType)" class="check-icon"/>
            <!-- Info Icon -->
            <info-icon v-else/>
            <!-- Text -->
            <text-block-generic
            :params="{
                label: aliasTypeLabel,
                content: aliasList.find(el => el.aliasType === aliasType) ? aliasList.find(el => el.aliasType === aliasType).alias : t('transfers.kwik.accountManagement.notFound')
            }"/>
        </div>
        <!-- Is Default Button -->
        <button-tertiary
        v-if="aliasList.find(el => el.aliasType === aliasType && el.default) && pendingAlias?.aliasType !== aliasType"
        buttonSize="small"
        class="row-button"
        disabled
        :label="t('transfers.kwik.accountManagement.isDefault')"/>
        <!-- Make Default Button -->
        <button-tertiary
        v-if="canBeDefault && aliasList.find(el => el.aliasType === aliasType && !el.default) && pendingAlias?.aliasType !== aliasType"
        buttonSize="small"
        class="row-button"
        :label="t('transfers.kwik.accountManagement.makeDefault')"
        @clicked="emit('changeDefaultAliasTypeHandler', aliasType)"/>
        <!-- Validate Button -->
        <button-primary
        v-else-if="aliasList.find(el => el.aliasType === aliasType) && pendingAlias?.aliasType === aliasType"
        buttonSize="small"
        class="row-button"
        :label="t('transfers.kwik.accountManagement.validateKey')"
        @clicked="emit('validateKeyHandler', authMethodtype)"/>
        <!-- Add Alias Button -->
        <button-primary
        v-else-if="!aliasList.find(el => el.aliasType === aliasType) && !pendingAlias"
        buttonSize="small"
        class="row-button"
        :label="t('transfers.kwik.accountManagement.addKey')"
        @clicked="emit('changeAliasTypeHandler', aliasType)"/>
        <!-- More Options -->
        <dropdown-more-options
        icon="more"
        :disabled="!aliasList.find(el => el.aliasType === aliasType) || (aliasList.find(el => el.aliasType === aliasType) && pendingAlias?.aliasType === aliasType)"
        :options="[{
            id: 'remove',
            text: t('transfers.kwik.accountManagement.remove'),
            icon: 'delete',
            variant: 'negative'
        }]"
        @clicked="emit('removeAliasTypeHandler', aliasType)"/>
    </div>
</template>

<script setup>
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InfoIcon from '@/assets/icons/info.svg?inline'
import CheckIcon from '@/assets/icons/check-circle.svg?inline'
import WarningIcon from '@/assets/icons/warning.svg?inline'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'

/* Vue */
import { computed } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['changeAliasTypeHandler', 'validateKeyHandler', 'changeDefaultAliasTypeHandler', 'removeAliasTypeHandler'])

/** Props
@canBeDefault {Boolean} Boolean to allow default button to be showed when other conditions met
@aliasType {String} Type of alias
@aliasList {Array} List of alias available
@aliasTypeLabel {String} Alias type label
*/
const props = defineProps({
    canBeDefault: Boolean,
	aliasType: String,
	aliasList: Array,
    aliasTypeLabel: String
})

const { getMobileAppState } = mapGetters()

const pendingAlias = computed(() => {
    return props.aliasList.find(el => el.aliasStatus?.aliasStatus === 'F')
})
</script>

<style lang="scss" scoped>
.alias-row {
    width: 100%;
    height: auto;
    padding: 16px 0;

    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    &:not(:last-of-type) {
        border-bottom: 1px solid var(--secondary-light-1);
    }

    .icon-text-wrapper {
        width: 100%;
        height: 40px;

        display: flex;
        align-items: center;
        gap: 16px;

        svg {
            width: 24px;
            height: 24px;
            flex-shrink: 0;

            :deep(.change-color-fill) {
                fill: var(--secondary);
            }

            &.check-icon {
                :deep(.change-color-fill) {
                    fill: var(--positive);
                }
            }

            &.warning-icon {
                :deep(.change-color-fill) {
                    fill: var(--warning);
                }
            }
        }

        :deep(label) {
            font-size: 14px;
            font-weight: 500;
            color: var(--secondary-dark-1);
        }
    }

    :deep(button) {
        &.row-button {
            width: 126px;
        }
    }

    .row-button {
        :deep(button) {
            width: 126px;
        }
    }

    &.mobile {
        flex-wrap: wrap;

        .icon-text-wrapper {
            width: calc(100% - 54px);

            :deep(.content-wrapper) {
                margin-top: 4px;
            }
        }

        .row-button {
            margin-left: 40px;
            order: 3;
        }
    }
}
</style>
